/*
 * TanStack Query integration: stable query keys + ready-made hooks over the
 * typed endpoint helpers. Centralising keys here keeps cache invalidation
 * consistent across U23/U24/U25 (e.g. a control mutation invalidates the run
 * list + that run's detail).
 *
 * These are thin and fully typed — hooks infer their data type from the
 * endpoint helper's return type, so no `any` crosses the boundary.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import {
  listRuns,
  getRun,
  createRun,
  pauseRun,
  resumeRun,
  redriveRun,
  cancelRun,
  forceCancelRun,
  confirmRun,
  getRunInconsistencies,
  getMyMemberships,
  previewLaunchTickets,
  refreshLaunchTickets,
  getLaunchProfiles,
  getLaunchCampaigns,
  getLaunchLandingPages,
  getLaunchAdsets,
  getLastRunDefaults,
  getRunPreview,
  getHygieneFlags,
  getVideoSync,
  getVideoTicketPerformance,
  setVideoSyncMode,
  triggerHygieneScan,
  getBookkeepDebts,
  dismissBookkeepDebt,
  getCoverageList,
  getCoverageSnapshot,
  getCoverageSyncStatus,
  postCoverageBrief,
  triggerCoverageSync,
  listConceptAliases,
  teachConceptAlias,
  deleteConceptAlias,
  suggestConceptAlias,
  listSavedReports,
  createSavedReport,
  updateSavedReport,
  deleteSavedReport,
} from "./endpoints";
import type {
  RunListResponse,
  RunDetail,
  RunCreate,
  RunConfirm,
  ControlResponse,
  InconsistencyReportResponse,
  MembershipResponse,
  PreviewTicketsResponse,
  NotionTicketRow,
  ProfilesResponse,
  CampaignsResponse,
  AdsetsResponse,
  LastRunDefaultsResponse,
  RunPreviewResponse,
  HygieneFlagsResponse,
  VideoSyncMode,
  VideoSyncModeResponse,
  VideoSyncResponse,
  HygieneScanResponse,
  BookkeepDebtsResponse,
  CoverageListResponse,
  CoverageSnapshotResponse,
  CoverageSyncAccepted,
  CoverageSyncStatusResponse,
  ConceptAliasRow,
  ConceptAliasListResponse,
  TeachConceptAliasRequest,
  ConceptAliasSuggestResponse,
  SavedReportRow,
  SavedReportListResponse,
} from "./client";

/** Server-side profiles cache TTL (15 min) — match it as the query staleTime. */
const PROFILES_STALE_MS = 15 * 60 * 1000;

/** Centralised, type-safe query keys. */
export const queryKeys = {
  memberships: ["memberships"] as const,
  // Prefix for ALL run-list pages — invalidating it refetches every page (U10).
  runs: ["runs"] as const,
  runsList: (limit: number, offset: number) => ["runs", "list", limit, offset] as const,
  run: (id: string) => ["runs", id] as const,
  runInconsistencies: (id: string) => ["runs", id, "inconsistencies"] as const,
  launchTickets: (productKey: string, flow: string, format: string) =>
    ["launch", "tickets", productKey, flow, format] as const,
  launchProfiles: (productKey: string) =>
    ["launch", "profiles", productKey] as const,
  launchCampaigns: (productKey: string) =>
    ["launch", "campaigns", productKey] as const,
  launchLandingPages: (productKey: string) =>
    ["launch", "landing-pages", productKey] as const,
  launchAdsets: (productKey: string, campaignId: string) =>
    ["launch", "adsets", productKey, campaignId] as const,
  // Settings prefill from the last run (2026-07-07 #4).
  lastRunDefaults: (productKey: string) =>
    ["launch", "last-defaults", productKey] as const,
  runPreview: (id: string) => ["runs", id, "preview"] as const,
  // Hygiene scan (plan 2026-07-02-001): the flagged-tickets list.
  hygieneFlags: ["hygiene", "flags"] as const,
  // Video ticket sync (2026-10-12): mode + per-ticket results.
  videoSync: ["video-sync"] as const,
  videoTicketPerformance: (id: string, today: boolean) => ["video-sync", "performance", id, today] as const,
  // Truth-debt ledger (batch 5 #8).
  bookkeepDebts: ["hygiene", "debts"] as const,
  // Creative Analysis coverage (plan 2026-07-22-001): the per-product
  // snapshot-summary list + one key per product's full explorer blob.
  coverageList: ["coverage", "list"] as const,
  coverageSnapshot: (productId: string) =>
    ["coverage", "snapshot", productId] as const,
  // Saved reports (2026-10-28).
  savedReports: (productId: string) => ["saved-reports", productId] as const,
  // Concept-alias ledger (batch 5 #7).
  conceptAliases: (productKey: string) => ["concept-aliases", productKey] as const,
  conceptAliasSuggest: (productKey: string, concept: string) =>
    ["concept-aliases", "suggest", productKey, concept] as const,
} as const;

export function useMemberships(
  options?: Partial<UseQueryOptions<MembershipResponse[]>>,
) {
  return useQuery({
    queryKey: queryKeys.memberships,
    queryFn: getMyMemberships,
    ...options,
  });
}

export function useRuns(
  limit: number,
  offset: number,
  options?: Partial<UseQueryOptions<RunListResponse>>,
) {
  return useQuery({
    queryKey: queryKeys.runsList(limit, offset),
    queryFn: () => listRuns(limit, offset),
    ...options,
  });
}

export function useRun(
  runId: string,
  options?: Partial<UseQueryOptions<RunDetail>>,
) {
  return useQuery({
    queryKey: queryKeys.run(runId),
    queryFn: () => getRun(runId),
    ...options,
  });
}

export function useRunInconsistencies(
  runId: string,
  options?: Partial<UseQueryOptions<InconsistencyReportResponse>>,
) {
  return useQuery({
    queryKey: queryKeys.runInconsistencies(runId),
    queryFn: () => getRunInconsistencies(runId),
    ...options,
  });
}

/** The brand's currently-flagged hygiene tickets (plan 2026-07-02-001). */
export function useHygieneFlags(
  options?: Partial<UseQueryOptions<HygieneFlagsResponse>>,
) {
  return useQuery({
    queryKey: queryKeys.hygieneFlags,
    queryFn: getHygieneFlags,
    ...options,
  });
}

/** The video sync mode + what it did (or would do) per video ticket. */
export function useVideoSync(options?: Partial<UseQueryOptions<VideoSyncResponse>>) {
  return useQuery({
    queryKey: queryKeys.videoSync,
    queryFn: getVideoSync,
    ...options,
  });
}

/** How a launched video is doing (2026-10-22): fetched when the hover opens.
 *  `today` asks for the slow live read; without it only the stored days. */
export function useVideoTicketPerformance(id: string, enabled: boolean, today = false) {
  return useQuery({
    queryKey: queryKeys.videoTicketPerformance(id, today),
    queryFn: () => getVideoTicketPerformance(id, today),
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/** Switch the server-wide video sync mode (super-admin), then re-read it. */
export function useSetVideoSyncMode() {
  const qc = useQueryClient();
  return useMutation<VideoSyncModeResponse, unknown, VideoSyncMode>({
    mutationFn: (mode) => setVideoSyncMode(mode),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.videoSync });
    },
  });
}

/** Enqueue an immediate hygiene scan, then refresh the flagged list + debts. */
export function useHygieneScanNow() {
  const qc = useQueryClient();
  return useMutation<HygieneScanResponse, unknown, void>({
    mutationFn: () => triggerHygieneScan(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.hygieneFlags });
      void qc.invalidateQueries({ queryKey: queryKeys.bookkeepDebts });
    },
  });
}

/** The brand's open truth-debts (batch 5 #8) — an empty list IS healthy. */
export function useBookkeepDebts(
  options?: Partial<UseQueryOptions<BookkeepDebtsResponse>>,
) {
  return useQuery({
    queryKey: queryKeys.bookkeepDebts,
    queryFn: getBookkeepDebts,
    ...options,
  });
}

/** Dismiss a hand-resolved debt, then refresh the ledger. */
export function useDismissBookkeepDebt() {
  const qc = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: (debtId) => dismissBookkeepDebt(debtId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.bookkeepDebts });
    },
  });
}

/* ---- Concept-alias ledger (batch 5 #7) --------------------------------- */

/** The brand's taught aliases (optionally one product's). */
export function useConceptAliases(
  productKey = "",
  options?: Partial<UseQueryOptions<ConceptAliasListResponse>>,
) {
  return useQuery({
    queryKey: queryKeys.conceptAliases(productKey),
    queryFn: () => listConceptAliases(productKey || undefined),
    ...options,
  });
}

/**
 * Sheet-backed teach candidates for one concept — fetched when the teach modal
 * opens. Not retried: a 400/424 is an actionable config problem, not churn.
 */
export function useConceptAliasSuggest(
  productKey: string,
  concept: string,
  enabled: boolean,
  options?: Partial<UseQueryOptions<ConceptAliasSuggestResponse>>,
) {
  return useQuery({
    queryKey: queryKeys.conceptAliasSuggest(productKey, concept),
    queryFn: () => suggestConceptAlias({ productKey, concept }),
    enabled: enabled && productKey.length > 0 && concept.length > 0,
    retry: false,
    ...options,
  });
}

/** Teach an alias, then refresh every alias list. */
export function useTeachConceptAlias() {
  const qc = useQueryClient();
  return useMutation<ConceptAliasRow, unknown, TeachConceptAliasRequest>({
    mutationFn: (body) => teachConceptAlias(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["concept-aliases"] });
    },
  });
}

/** Forget an alias, then refresh every alias list. */
export function useDeleteConceptAlias() {
  const qc = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: (aliasId) => deleteConceptAlias(aliasId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["concept-aliases"] });
    },
  });
}

/* ---- Creative Analysis coverage (plan 2026-07-22-001, U7) --------------- */

/** Every product + its latest snapshot summary (no payloads — cheap). */
export function useCoverageList(
  options?: Partial<UseQueryOptions<CoverageListResponse>>,
) {
  return useQuery({
    queryKey: queryKeys.coverageList,
    queryFn: getCoverageList,
    ...options,
  });
}

/**
 * One product's latest full snapshot payload — keyed PER PRODUCT so a
 * product switch is always a fresh pending state (the page renders a
 * skeleton, never the previous product's data). A 404 is the page's empty
 * state (the U5 no-snapshot contract), so it is never retried; the blobs are
 * multi-MB, so a generous staleTime keeps back-and-forth switching cheap.
 */
export function useCoverageSnapshot(
  productId: string,
  options?: Partial<UseQueryOptions<CoverageSnapshotResponse>>,
) {
  return useQuery({
    queryKey: queryKeys.coverageSnapshot(productId),
    queryFn: () => getCoverageSnapshot(productId),
    enabled: productId.length > 0,
    retry: false,
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    ...options,
  });
}

/**
 * Enqueue an immediate coverage recompute (operator, 202), then refresh the
 * list — the page keeps polling it until the recomputed product's
 * `computed_at` advances (or its timeout lapses).
 */
/** Draft a Pipeline ticket from a whitespace cell (scaffolder, 2026-07-29). */
export function useCoverageBrief() {
  return useMutation<
    { created: boolean; page_id: string; url: string | null },
    unknown,
    { product_id: string; angle: string; avatar: string; awareness: string; note?: string }
  >({
    mutationFn: (body) => postCoverageBrief(body),
  });
}

/**
 * Live recompute state (2026-08-18) — polled fast while a sync is in flight
 * so the page can show "running since…" or the retained failure reason
 * instead of a stale banner that never explains itself.
 */
export function useCoverageSyncStatus(
  productId: string | undefined,
  options?: Partial<UseQueryOptions<CoverageSyncStatusResponse>>,
) {
  return useQuery({
    queryKey: ["coverage", "sync-status", productId ?? "brand"] as const,
    queryFn: () => getCoverageSyncStatus(productId),
    retry: false,
    ...options,
  });
}

export function useCoverageSyncNow() {
  const qc = useQueryClient();
  return useMutation<
    CoverageSyncAccepted,
    unknown,
    { start?: string; end?: string; product_id?: string } | undefined
  >({
    mutationFn: (opts) => triggerCoverageSync(opts),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.coverageList });
    },
  });
}

/* ---- Saved reports (2026-10-28) ---------------------------------------- */

export function useSavedReports(productId: string, options?: Partial<UseQueryOptions<SavedReportListResponse>>) {
  return useQuery({
    queryKey: queryKeys.savedReports(productId),
    queryFn: () => listSavedReports(productId),
    enabled: productId.length > 0,
    ...options,
  });
}

export function useCreateSavedReport() {
  const qc = useQueryClient();
  return useMutation<SavedReportRow, unknown, { product_id: string; name: string; state: Record<string, unknown> }>({
    mutationFn: (body) => createSavedReport(body),
    onSuccess: (row) => {
      void qc.invalidateQueries({ queryKey: queryKeys.savedReports(row.product_id) });
    },
  });
}

export function useUpdateSavedReport() {
  const qc = useQueryClient();
  return useMutation<SavedReportRow, unknown, { id: string; name?: string; state?: Record<string, unknown> }>({
    mutationFn: ({ id, ...body }) => updateSavedReport(id, body),
    onSuccess: (row) => {
      void qc.invalidateQueries({ queryKey: queryKeys.savedReports(row.product_id) });
    },
  });
}

export function useDeleteSavedReport() {
  const qc = useQueryClient();
  return useMutation<void, unknown, { id: string; productId: string }>({
    mutationFn: ({ id }) => deleteSavedReport(id),
    onSuccess: (_v, { productId }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.savedReports(productId) });
    },
  });
}

/** Create a run, then refresh the queue. */
export function useCreateRun() {
  const qc = useQueryClient();
  return useMutation<RunDetail, unknown, RunCreate>({
    mutationFn: (body) => createRun(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.runs });
    },
  });
}

/**
 * Live Notion ticket preview for the launch wizard (Step 2). Gated by `enabled`
 * so it only fires once a product is chosen and the step is reached. `flow` is
 * the (empty ⇒ no-filter) flow value; it's part of the cache key so switching
 * the flow refetches. The fetch may fail with a Notion-auth error (424/502) —
 * the caller surfaces it with a retry affordance. Not retried automatically so
 * a credential error doesn't silently churn.
 */
export function useLaunchTickets(
  productKey: string,
  flow: string,
  format: string,
  enabled: boolean,
  options?: Partial<UseQueryOptions<PreviewTicketsResponse>>,
) {
  return useQuery({
    queryKey: queryKeys.launchTickets(productKey, flow, format),
    queryFn: () => previewLaunchTickets({ productKey, flow, format }),
    enabled: enabled && productKey.length > 0,
    retry: false,
    ...options,
  });
}

type ControlAction = "pause" | "resume" | "redrive" | "cancel" | "force-cancel";

const CONTROL_FN: Record<ControlAction, (id: string) => Promise<ControlResponse>> = {
  pause: pauseRun,
  resume: resumeRun,
  redrive: redriveRun,
  cancel: cancelRun,
  "force-cancel": forceCancelRun,
};

/**
 * Run control (pause/resume/cancel). On success invalidates both the run list
 * and that run's detail so the new status/control reflects immediately.
 */
export function useRunControl(runId: string) {
  const qc = useQueryClient();
  return useMutation<ControlResponse, unknown, ControlAction>({
    mutationFn: (action) => CONTROL_FN[action](runId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.run(runId) });
      void qc.invalidateQueries({ queryKey: queryKeys.runs });
    },
  });
}

/* ---- Launch wizard: refresh-selected + Admanage Settings + Preview ---- */

/**
 * Refresh-selected (Step 2): re-pull only the given page ids and merge the fresh
 * rows INTO the existing preview cache in place — preserving the server order and
 * the operator's selection (rows are matched by `page_id`; unlisted rows untouched).
 */
export function useRefreshSelectedTickets(productKey: string, flow: string, format: string) {
  const qc = useQueryClient();
  return useMutation<NotionTicketRow[], unknown, string[]>({
    mutationFn: (pageIds) => refreshLaunchTickets({ productKey, pageIds }),
    onSuccess: (rows) => {
      const byId = new Map(rows.map((r) => [r.page_id, r]));
      qc.setQueryData<PreviewTicketsResponse>(
        queryKeys.launchTickets(productKey, flow, format),
        (prev) =>
          prev
            ? { ...prev, tickets: prev.tickets.map((t) => byId.get(t.page_id) ?? t) }
            : prev,
      );
    },
  });
}

/**
 * Admanage page picker (Step 3). `staleTime` matches the server's 15-min cache.
 * Not retried — a 424 credential error must surface, not churn.
 */
export function useLaunchProfiles(
  productKey: string,
  enabled: boolean,
  options?: Partial<UseQueryOptions<ProfilesResponse>>,
) {
  return useQuery({
    queryKey: queryKeys.launchProfiles(productKey),
    queryFn: () => getLaunchProfiles(productKey),
    enabled: enabled && productKey.length > 0,
    staleTime: PROFILES_STALE_MS,
    retry: false,
    ...options,
  });
}

/** Admanage campaign picker (Step 3) — Active + Scheduled. */
/**
 * The URL field's dropdown (Step 3, 2026-10-19): the product's landing pages
 * from the Notion Landing Pages table. A failed read just leaves the field
 * free-text, so no retry.
 */
export function useLaunchLandingPages(productKey: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.launchLandingPages(productKey),
    queryFn: () => getLaunchLandingPages(productKey),
    enabled: enabled && productKey.length > 0,
    retry: false,
    staleTime: 5 * 60_000,
  });
}

export function useLaunchCampaigns(
  productKey: string,
  enabled: boolean,
  options?: Partial<UseQueryOptions<CampaignsResponse>>,
) {
  return useQuery({
    queryKey: queryKeys.launchCampaigns(productKey),
    queryFn: () => getLaunchCampaigns(productKey),
    enabled: enabled && productKey.length > 0,
    retry: false,
    ...options,
  });
}

/**
 * Admanage adset picker (Step 3) — gated on a chosen campaign: the query is
 * `enabled` only once `campaignId` is set, so changing the campaign refetches.
 */
/**
 * Settings prefill (2026-07-07 #4): the last run's page / URL / campaign /
 * adset for the product. MUST be fresh per wizard mount: the prefill effect
 * applies the FIRST data it sees and then pins itself, so a cached response
 * from a previous wizard (whose "last run" is now second-to-last) would seed
 * the wrong campaign/adset. `staleTime: 0` overrides the app-level 15s
 * default; `gcTime: 0` drops the cache when the wizard unmounts, so the
 * next wizard always fetches — a run launched minutes ago seeds the next one.
 */
export function useLastRunDefaults(
  productKey: string,
  enabled: boolean,
  options?: Partial<UseQueryOptions<LastRunDefaultsResponse>>,
) {
  return useQuery({
    queryKey: queryKeys.lastRunDefaults(productKey),
    queryFn: () => getLastRunDefaults(productKey),
    enabled: enabled && productKey.length > 0,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    ...options,
  });
}

export function useLaunchAdsets(
  productKey: string,
  campaignId: string,
  options?: Partial<UseQueryOptions<AdsetsResponse>>,
) {
  return useQuery({
    queryKey: queryKeys.launchAdsets(productKey, campaignId),
    queryFn: () => getLaunchAdsets(productKey, campaignId),
    enabled: productKey.length > 0 && campaignId.length > 0,
    retry: false,
    // U2 — cache adsets for 15 min so revisiting the Tickets/Defaults step does
    // not re-hit AdManage every time. gcTime must exceed staleTime or the cache
    // can be garbage-collected (RQ default gcTime is 5 min) before it goes stale.
    staleTime: 15 * 60_000,
    gcTime: 20 * 60_000,
    ...options,
  });
}

/**
 * The prepped per-ad preview for a gated run (Step 4). Enabled by the caller once
 * the run exists; the caller typically polls/enables it after the WS stream shows
 * `AWAITING_CONFIRM`.
 */
export function useRunPreview(
  runId: string,
  enabled: boolean,
  options?: Partial<UseQueryOptions<RunPreviewResponse>>,
) {
  return useQuery({
    queryKey: queryKeys.runPreview(runId),
    queryFn: () => getRunPreview(runId),
    enabled: enabled && runId.length > 0,
    ...options,
  });
}

/** Confirm (release) a gated run; invalidates the run detail + the queue list. */
export function useConfirmRun(runId: string) {
  const qc = useQueryClient();
  return useMutation<ControlResponse, unknown, RunConfirm | undefined>({
    mutationFn: (body) => confirmRun(runId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.run(runId) });
      void qc.invalidateQueries({ queryKey: queryKeys.runs });
    },
  });
}
